import {
  ArrowPathIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon,
  GlobeAltIcon,
  ListBulletIcon,
  PaintBrushIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearSessionStorage } from '@/shared/lib/storage';
import { openExternalUrl } from '@/shared/lib/utils/url';
import { useStickyNoteStore } from '@/shared/stores/sticky-note-store';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/shared/ui';
import { ReleaseFlagControlDialog } from './ReleaseFlagControlDialog';
import { StickyNoteListDialog } from './StickyNoteListDialog';
import { UIControlDialog } from './UIControlDialog';

/**
 * デバッグメニュー
 * isDebugEnabled() 時のみ表示される開発用メニュー
 * Cmd/Ctrl + K でコマンドパレットを開く
 */
export const DebugMenu = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dialogType, setDialogType] = useState<
    'releaseFlag' | 'uiControl' | 'stickyNoteList' | null
  >(null);
  const isVisible = useStickyNoteStore((s) => s.isVisible);

  // Cmd/Ctrl + K でコマンドパレットを開く
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  return (
    <>
      {/* コマンドダイアログ */}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="コマンドを検索..." />
        <CommandList>
          <CommandEmpty>コマンドが見つかりません。</CommandEmpty>
          <CommandGroup heading="外部リンク">
            <CommandItem
              onSelect={() => {
                openExternalUrl('https://github.com/weldrow-com/dnr');
                setOpen(false);
              }}
            >
              <GlobeAltIcon className="mr-2 h-4 w-4" />
              <span>GitHub</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                openExternalUrl(
                  'https://www.notion.so/wisevine-recruit/24fa8fe146e780479197c6935c6f865e?v=24fa8fe146e78072bb7c000cbed24b25&source=copy_link',
                );
                setOpen(false);
              }}
            >
              <DocumentTextIcon className="mr-2 h-4 w-4" />
              <span>機能一覧(Notion)</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                openExternalUrl(
                  'https://www.figma.com/files/1417563749368031754/project/465851970?fuid=1312068227037450585',
                );
                setOpen(false);
              }}
            >
              <SwatchIcon className="mr-2 h-4 w-4" />
              <span>Figma</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                openExternalUrl('https://dnr-support.pages.dev/');
                setOpen(false);
              }}
            >
              <BookOpenIcon className="mr-2 h-4 w-4" />
              <span>開発ドキュメント</span>
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="システム">
            <CommandItem
              onSelect={() => {
                openExternalUrl('https://dev.dnr.wise-vine.com');
                setOpen(false);
              }}
            >
              <GlobeAltIcon className="mr-2 h-4 w-4" />
              <span>dev</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                openExternalUrl('https://stg.dnr.wise-vine.com');
                setOpen(false);
              }}
            >
              <GlobeAltIcon className="mr-2 h-4 w-4" />
              <span>stg</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                openExternalUrl('https://admin.dev.dnr.wise-vine.com');
                setOpen(false);
              }}
            >
              <GlobeAltIcon className="mr-2 h-4 w-4" />
              <span>dev管理画面</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                openExternalUrl('https://admin.stg.dnr.wise-vine.com');
                setOpen(false);
              }}
            >
              <GlobeAltIcon className="mr-2 h-4 w-4" />
              <span>stg管理画面</span>
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="付箋">
            <CommandItem
              onSelect={() => {
                useStickyNoteStore.getState().setAddMode(true);
                setOpen(false);
              }}
            >
              <PaintBrushIcon className="mr-2 h-4 w-4" />
              <span>{t('debug-menu.sticky-note.add')}</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                useStickyNoteStore.getState().toggleVisibility();
                setOpen(false);
              }}
            >
              {isVisible ? (
                <EyeIcon className="mr-2 h-4 w-4" />
              ) : (
                <EyeSlashIcon className="mr-2 h-4 w-4" />
              )}
              <span>{t('debug-menu.sticky-note.toggle-visibility')}</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setOpen(false);
                setDialogType('stickyNoteList');
              }}
            >
              <ListBulletIcon className="mr-2 h-4 w-4" />
              <span>{t('debug-menu.sticky-note.list')}</span>
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="設定">
            <CommandItem
              onSelect={() => {
                setOpen(false);
                setDialogType('uiControl');
              }}
            >
              <Cog6ToothIcon className="mr-2 h-4 w-4" />
              <span>{t('debug-menu.ui-control')}</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setOpen(false);
                setDialogType('releaseFlag');
              }}
            >
              <Cog6ToothIcon className="mr-2 h-4 w-4" />
              <span>{t('debug-menu.release-flag-control')}</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                clearSessionStorage();
                window.location.reload();
              }}
            >
              <ArrowPathIcon className="mr-2 h-4 w-4" />
              <span>{t('debug-menu.reset')}</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* UI制御ダイアログ */}
      <UIControlDialog
        open={dialogType === 'uiControl'}
        onOpenChange={(open) => !open && setDialogType(null)}
      />
      {/* Release Flag制御ダイアログ */}
      <ReleaseFlagControlDialog
        open={dialogType === 'releaseFlag'}
        onOpenChange={(open) => !open && setDialogType(null)}
      />
      {/* 付箋一覧ダイアログ */}
      <StickyNoteListDialog
        open={dialogType === 'stickyNoteList'}
        onOpenChange={(open) => !open && setDialogType(null)}
      />
    </>
  );
};
